import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, CheckCircle, XCircle } from 'lucide-react';

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="refund-policy-page">
      {/* Breadcrumb */}
      <div className="bg-[#FFF9F5] border-b">
        <div className="container-main py-4">
          <nav className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-brand-orange">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 font-medium">Refund & Return Policy</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="bg-gradient-to-br from-[#FFF9F5] to-[#FFE8D4] py-12">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Refund & Return Policy
            </h1>
            <p className="text-gray-600">Last updated: January 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            {/* Highlight Box */}
            <div className="bg-brand-orange-light p-6 rounded-3xl mb-8">
              <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">
                30-Day Hassle-Free Returns
              </h2>
              <p className="text-gray-700">
                We want you and your pet to be completely satisfied with your purchase. If you're not happy for any reason, we offer a full 30-day return policy.
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Return Eligibility</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  To be eligible for a return, your item must meet the following conditions:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Eligible for Return</span>
                    </div>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• Returned within 30 days of delivery</li>
                      <li>• Item is unused and in original condition</li>
                      <li>• Item is in original packaging</li>
                      <li>• Proof of purchase included</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <span className="font-medium text-red-800">Not Eligible for Return</span>
                    </div>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Items used by pets</li>
                      <li>• Items without original packaging</li>
                      <li>• Items returned after 30 days</li>
                      <li>• Items marked as final sale</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">How to Initiate a Return</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-3">
                  <li><strong>Contact Us:</strong> Email us at support@nexafusion.store with your order number and reason for return</li>
                  <li><strong>Receive Return Authorization:</strong> We'll send you a return authorization and shipping instructions within 24 hours</li>
                  <li><strong>Ship the Item:</strong> Pack the item securely in its original packaging and ship it to our return address</li>
                  <li><strong>Receive Your Refund:</strong> Once we receive and inspect the item, we'll process your refund</li>
                </ol>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Refund Process</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Once we receive your returned item, we will inspect it and notify you of the status of your refund.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Refunds will be processed within 5-7 business days of receiving the returned item</li>
                  <li>For COD orders, refunds will be issued via bank transfer or store credit</li>
                  <li>Original shipping costs are non-refundable unless the return is due to our error</li>
                  <li>You will receive an email confirmation once your refund has been processed</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Return Shipping</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Return shipping costs depend on the reason for return:
                </p>
                <div className="bg-[#FFF9F5] p-6 rounded-2xl space-y-3">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-700">Defective or damaged item</span>
                    <span className="font-medium text-brand-green">Free (prepaid label provided)</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-700">Wrong item received</span>
                    <span className="font-medium text-brand-green">Free (prepaid label provided)</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-700">Change of mind</span>
                    <span className="font-medium text-gray-600">Customer pays return shipping</span>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Exchanges</h2>
                <p className="text-gray-600 leading-relaxed">
                  If you need to exchange an item for a different size, color, or product, please return the original item and place a new order. This ensures you get your new item as quickly as possible without waiting for the return to be processed.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Damaged or Defective Items</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If you receive a damaged or defective item:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Contact us within 48 hours of delivery</li>
                  <li>Include photos of the damage or defect</li>
                  <li>We'll arrange for a replacement or full refund at no additional cost to you</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Questions?</h2>
                <p className="text-gray-600 leading-relaxed">
                  If you have any questions about our return policy, please don't hesitate to contact us:
                </p>
                <div className="mt-4 bg-[#FFF9F5] p-6 rounded-2xl">
                  <p className="text-gray-700"><strong>NexaFusion Customer Support</strong></p>
                  <p className="text-gray-600">Email: support@nexafusion.store</p>
                  <p className="text-gray-600">Phone: +1-888-728-1766</p>
                  <p className="text-gray-600">Hours: Monday - Friday, 9am - 6pm EST</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicyPage;
